import { navRoleCeiling, type NavKey, type UserRole } from '@seenaly/shared';
import type { Dict } from '@/i18n';
import type { IconName } from '@/components/ui';

/**
 * Sidebar structure, in the order SKILL.md specifies.
 *
 * `key` doubles as the nav item's identity for the per-role menu-visibility
 * toggle admin controls in Settings (docs/spec.md §14 round 3, E8) — it must
 * match a key in `navRoleCeiling` (packages/shared/src/nav.ts), the ceiling
 * that toggle can narrow but never exceed.
 *
 * Hiding an item here is convenience, not security — the server decides
 * (spec §6). A user who types a forbidden URL still gets a 403 from the API.
 */

export interface NavItem {
  key: NavKey;
  to: string;
  labelKey: keyof Dict['nav'];
  icon: IconName;
  /** Which live counter to show as a pill, if any. */
  badge?: 'appointmentsToday' | 'lowStock';
}

export interface NavGroup {
  titleKey: 'menuMain' | 'menuReports';
  items: readonly NavItem[];
}

export const navGroups: readonly NavGroup[] = [
  {
    titleKey: 'menuMain',
    items: [
      { key: 'dashboard', to: '/', labelKey: 'dashboard', icon: 'dashboard' },
      {
        key: 'appointments',
        to: '/appointments',
        labelKey: 'appointments',
        icon: 'calendar',
        badge: 'appointmentsToday',
      },
      { key: 'patients', to: '/patients', labelKey: 'patients', icon: 'users' },
      { key: 'billing', to: '/invoices', labelKey: 'billing', icon: 'billing' },
      {
        key: 'inventory',
        to: '/inventory',
        labelKey: 'inventory',
        icon: 'inventory',
        badge: 'lowStock',
      },
      { key: 'medSale', to: '/pharmacy', labelKey: 'medSale', icon: 'medSale' },
      { key: 'lab', to: '/lab', labelKey: 'lab', icon: 'lab' },
    ],
  },
  {
    titleKey: 'menuReports',
    items: [
      { key: 'revenue', to: '/reports/revenue', labelKey: 'revenue', icon: 'chart' },
      { key: 'stockReport', to: '/reports/stock', labelKey: 'stockReport', icon: 'history' },
      { key: 'profitReport', to: '/reports/profit', labelKey: 'profitReport', icon: 'money' },
      { key: 'settings', to: '/settings', labelKey: 'settings', icon: 'settings' },
    ],
  },
];

/**
 * `visibleNavKeys` is the API's already-resolved answer (ceiling ∩ this
 * role's overrides) — the ceiling check here is defense in depth, not the
 * primary filter, in case that list is ever stale or missing.
 */
export function visibleGroups(role: UserRole, visibleNavKeys: readonly string[]): NavGroup[] {
  return navGroups
    .map((group) => ({
      ...group,
      items: group.items.filter(
        (item) => navRoleCeiling[item.key].includes(role) && visibleNavKeys.includes(item.key),
      ),
    }))
    .filter((group) => group.items.length > 0);
}
